// Case4Page.js
import React, { useEffect, useState, useRef } from 'react';
import { PencilIcon, BoltIcon, ChartBarIcon, LightBulbIcon } from '@heroicons/react/24/outline';
import case4Image from '../assets/RedRockCases/Case4/Case 4 placeholder.png';
import SEO from '../components/SEO';

const TIMER_WARNING_THRESHOLD = 0.8; // 80% of the way through

const Case4Page = () => {
  const [hasStarted, setHasStarted] = useState(false);
  const [timeLeft, setTimeLeft] = useState(1200);
  const [answers, setAnswers] = useState({});
  const [feedback, setFeedback] = useState({});
  const [reveal, setReveal] = useState({});
  const [timeExpired, setTimeExpired] = useState(false);
  const [finishedEarly, setFinishedEarly] = useState(false);
  const [isComplete, setIsComplete] = useState(false);
  const [notebookItems, setNotebookItems] = useState([]);
  const [isNotebookOpen, setIsNotebookOpen] = useState(true);
  const [hasBeenOpened, setHasBeenOpened] = useState(false);
  const timerRef = useRef(null);
  const [currentExhibit, setCurrentExhibit] = useState('1');

  const questions = [
    { 
      id: 1, 
      text: "Which district saw the highest increase in solar energy production between 2023 and 2024?",
      answer: "Central",
      type: "text",
      hint: "Look at the solar production data by district."
    },
    { 
      id: 2, 
      text: "Which income group accounted for the largest share of EV purchases?",
      answer: "Middle Income",
      type: "text",
      hint: "Check the EV adoption data by income group."
    },
    { 
      id: 3, 
      text: "In which month does off-peak usage surpass 400 kWh for the first time?",
      answer: "April",
      type: "text",
      hint: "Look at the power usage trends over time."
    },
    { 
      id: 4, 
      text: "Which program provides the most CO2 reduction per million euros spent?",
      answer: "Solar Panels",
      type: "text",
      hint: "Compare the cost-effectiveness of different programs."
    },
    { 
      id: 5, 
      text: "Which income group had the fewest solar panel adoptions?",
      answer: "Low Income",
      type: "text",
      hint: "Check the solar adoption data by income group."
    },
    { 
      id: 6, 
      text: "What is the total energy usage in April (solar + grid)?",
      answer: "500 kWh",
      type: "text",
      hint: "Calculate the combined energy usage."
    },
    { 
      id: 7, 
      text: "How many households adopted solar in the middle-income group?",
      answer: "340",
      type: "text",
      hint: "Look at the solar adoption numbers."
    },
    { 
      id: 8, 
      text: "What percentage of total solar adoptions came from high-income households?",
      answer: "31.6%",
      type: "text",
      hint: "Calculate the percentage from the adoption data."
    }
  ];

  useEffect(() => {
    if (!hasStarted || timeExpired || finishedEarly) return;
    timerRef.current = setInterval(() => {
      setTimeLeft((prev) => {
        if (prev <= 1) {
          clearInterval(timerRef.current);
          setTimeExpired(true);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(timerRef.current);
  }, [hasStarted, timeExpired, finishedEarly]);

  useEffect(() => {
    // Check if all questions are answered
    const allAnswered = hasStarted && !isComplete && Object.keys(feedback).length === 8;
    
    if (allAnswered) {
      setIsComplete(true);
      // If completed and time is still left, mark as finished early
      if (timeLeft > 0) {
        setFinishedEarly(true);
        // Optional: Stop the timer if finished early
        // clearInterval(timerRef.current);
      }
    }
    // Added questionData.length, timeLeft, and setFinishedEarly to dependencies
  }, [feedback, hasStarted, isComplete, timeLeft, setFinishedEarly]);

  const handleInput = (id, value, partIndex = null) => {
    if (timeExpired || !hasStarted || finishedEarly) return;
    if (partIndex !== null) {
      setAnswers((prev) => ({
        ...prev,
        [id]: { ...(prev[id] || {}), [partIndex]: value }
      }));
    } else {
    setAnswers((prev) => ({ ...prev, [id]: value }));
    }
  };

  const handleSubmit = (id) => {
    if (timeExpired || !hasStarted || finishedEarly) return;
    const q = questions.find((q) => q.id === id);
    
    if (q.type === 'multi') {
      const allPartsCorrect = q.parts.every((part, index) => {
        const userAnswer = ((answers[id] || {})[index] || '').trim().toLowerCase();
        return userAnswer === part.correctAnswer.toLowerCase();
      });
      setFeedback((prev) => ({ ...prev, [id]: allPartsCorrect ? 'correct' : 'incorrect' }));
    } else {
    const userAnswer = (answers[id] || '').trim().toLowerCase();
    const correctAnswer = q.answer.toLowerCase();
    setFeedback((prev) => ({ ...prev, [id]: userAnswer === correctAnswer ? 'correct' : 'incorrect' }));
    }
  };

  const handleTryAgain = (id) => {
    if (timeExpired || !hasStarted || finishedEarly) return;
    setAnswers((prev) => ({ ...prev, [id]: '' }));
    setFeedback((prev) => ({ ...prev, [id]: null }));
    setReveal((prev) => ({ ...prev, [id]: false }));
  };

  const handleReveal = (id) => {
    setReveal((prev) => ({ ...prev, [id]: true }));
  };

  const handleMCQSelect = (id, optionLabel) => {
    if (timeExpired || !hasStarted || finishedEarly) return;
    setAnswers((prev) => ({ ...prev, [id]: optionLabel }));
    const q = questions.find((q) => q.id === id);
    setFeedback((prev) => ({ ...prev, [id]: optionLabel === q.answer ? 'correct' : 'incorrect' }));
  };

  const formatTime = (secs) => {
    const m = Math.floor(secs / 60).toString().padStart(2, '0');
    const s = (secs % 60).toString().padStart(2, '0');
    return `${m}:${s}`;
  };

    const reportAnswers = {
      'report_solar_increase': '900',
    'report_income_group': 'Middle',
      'report_month': 'April',
      'report_efficiency': '2',
      'report_cost': '0.08'
    };
  const numReportQuestions = Object.keys(reportAnswers).length;
  const numAnalysisQuestions = questions.length;
  const numReportCorrect = Object.entries(feedback).filter(([k, v]) => v === 'correct' && k.startsWith('report_')).length;
  const numAnalysisCorrect = Object.entries(feedback).filter(([k, v]) => v === 'correct' && !k.startsWith('report_')).length;
  const totalQuestions = numReportQuestions + numAnalysisQuestions;
  const totalCorrect = numReportCorrect + numAnalysisCorrect;

  const handleReportSubmit = () => {
    if (timeExpired || !hasStarted || finishedEarly) return;
    clearInterval(timerRef.current);
    setTimeExpired(true);
    setFinishedEarly(true);
    const newFeedback = {};
    Object.entries(reportAnswers).forEach(([key, correctAnswer]) => {
      const userAnswer = (answers[key] || '').trim();
      newFeedback[key] = userAnswer.toLowerCase() === correctAnswer.toLowerCase() ? 'correct' : 'incorrect';
    });
    setFeedback(prev => ({ ...prev, ...newFeedback, report: 'submitted' }));
    setReveal(prev => ({ ...prev, report: true }));
  };

  const handleReportReveal = () => {
    setReveal(prev => ({ ...prev, report: true }));
  };

  const handleDragStart = (e, value, label) => {
    if (e.target.closest('.notebook-content')) return;
    const data = {
      value: value,
      label: label
    };
    e.dataTransfer.setData('application/json', JSON.stringify(data));
  };

  const handleDrop = (e) => {
    if (!hasStarted) return;
    e.preventDefault();
    try {
      const data = e.dataTransfer.getData('application/json');
      if (!data) return;
      
      const parsedData = JSON.parse(data);
      if (parsedData.value && parsedData.label) {
        setNotebookItems(prev => [...prev, parsedData]);
      }
    } catch (error) {
      console.error('Error handling drop:', error);
    }
    e.currentTarget.classList.remove('bg-blue-50');
  };

  const removeNotebookItem = (index) => {
    setNotebookItems(prev => prev.filter((_, i) => i !== index));
  };

  const handleNotebookToggle = () => {
    if (!isNotebookOpen) {
      setHasBeenOpened(true);
    }
    setIsNotebookOpen(!isNotebookOpen);
  };

  return (
    <div className="bg-white text-gray-900 font-sans relative scroll-pt-[400px] scroll-smooth">
      <SEO noIndex={true} />
      {/* Mobile Message - Only visible on mobile */}
      <div className="md:hidden min-h-screen flex items-center justify-center p-6 bg-gradient-to-br from-blue-200 to-white">
        <div className="text-center space-y-4">
          <div className="w-20 h-20 mx-auto mb-4 bg-white rounded-full flex items-center justify-center shadow-lg">
            <svg className="w-10 h-10 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z" />
            </svg>
          </div>
          <h1 className="text-2xl font-bold text-gray-900">Desktop Required</h1>
          <p className="text-gray-700 max-w-sm mx-auto">
            Cases are designed for desktop and laptop computers. Please use a larger screen to access this case study.
          </p>
          <div className="pt-4">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-blue-50 rounded-lg text-blue-700">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <span className="text-sm">Best viewed on screens wider than 768px</span>
            </div>
          </div>
        </div>
      </div>

      {/* Desktop Content - Hidden on mobile */}
      <div className="hidden md:block">
        {/* Side Navigation */}
        <div className="fixed right-6 top-[285px] flex flex-col gap-6 z-[9999]">
          <div className="group relative">
          <a 
            href="#problem" 
              className="flex items-center"
            aria-label="Problem Section"
          >
              <div className="absolute right-full mr-2 px-2 py-1 rounded bg-white/80 backdrop-blur-sm text-sm text-gray-600 font-medium opacity-0 -translate-x-2 transition-all duration-200 whitespace-nowrap shadow-sm group-hover:opacity-100 group-hover:translate-x-0 z-[10000]">
              Problem
            </div>
            <div className="p-2 rounded-lg bg-white shadow-md hover:shadow-lg transition-shadow">
              <PencilIcon className="w-5 h-5 text-gray-400" />
            </div>
          </a>
          </div>

          <div className="group relative">
            <a 
              href="#investigation" 
              className="flex items-center"
              aria-label="Investigation Section"
            >
              <div className="absolute right-full mr-2 px-2 py-1 rounded bg-white/80 backdrop-blur-sm text-sm text-gray-600 font-medium opacity-0 -translate-x-2 transition-all duration-200 whitespace-nowrap shadow-sm group-hover:opacity-100 group-hover:translate-x-0 z-[10000]">
                Investigation
            </div>
            <div className="p-2 rounded-lg bg-white shadow-md hover:shadow-lg transition-shadow">
              <BoltIcon className="w-5 h-5 text-gray-400" />
            </div>
          </a>
          </div>

          <div className="group relative">
            <a 
              href="#exhibits" 
              className="flex items-center"
              aria-label="Exhibits Section"
            >
              <div className="absolute right-full mr-2 px-2 py-1 rounded bg-white/80 backdrop-blur-sm text-sm text-gray-600 font-medium opacity-0 -translate-x-2 transition-all duration-200 whitespace-nowrap shadow-sm group-hover:opacity-100 group-hover:translate-x-0 z-[10000]">
                Exhibits
              </div>
              <div className="p-2 rounded-lg bg-white shadow-md hover:shadow-lg transition-shadow">
                <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                </svg>
              </div>
            </a>
          </div>

          <div className="group relative">
            <a 
              href="#questions" 
              className="flex items-center"
              aria-label="Analysis Section"
            >
              <div className="absolute right-full mr-2 px-2 py-1 rounded bg-white/80 backdrop-blur-sm text-sm text-gray-600 font-medium opacity-0 -translate-x-2 transition-all duration-200 whitespace-nowrap shadow-sm group-hover:opacity-100 group-hover:translate-x-0 z-[10000]">
                Analysis
            </div>
            <div className="p-2 rounded-lg bg-white shadow-md hover:shadow-lg transition-shadow">
              <ChartBarIcon className="w-5 h-5 text-gray-400" />
            </div>
          </a>
          </div>

          <div className="group relative">
          <a 
            href="#recommendation" 
              className="flex items-center"
            aria-label="Recommendation Section"
          >
              <div className="absolute right-full mr-2 px-2 py-1 rounded bg-white/80 backdrop-blur-sm text-sm text-gray-600 font-medium opacity-0 -translate-x-2 transition-all duration-200 whitespace-nowrap shadow-sm group-hover:opacity-100 group-hover:translate-x-0 z-[10000]">
              Recommendation
            </div>
            <div className="p-2 rounded-lg bg-white shadow-md hover:shadow-lg transition-shadow">
              <LightBulbIcon className="w-5 h-5 text-gray-400" />
            </div>
          </a>
          </div>
        </div>

        {/* Hero Section - Enhanced gradient */}
        <section className="sticky top-[50px] z-30 bg-gradient-to-br from-blue-200 to-white pb-4 relative">
          <div className="container pl-[72px] grid grid-cols-1 md:grid-cols-2 gap-8 pt-16">
            <div>
              <h1 className="text-3xl md:text-4xl font-bold mb-3">Case 4: SolarFlux City Initiative</h1>
              <p className="text-base md:text-lg text-gray-700">Determine whether SolarFlux City's sustainability initiatives are reducing emissions efficiently, and recommend whether to expand or modify the program. You must synthesize the most relevant data and choose the right visualizations to make your case to the council.</p>
              <div className="mt-4 mb-4 p-3 bg-blue-50 rounded-lg border border-blue-100">
                <p className="text-sm text-blue-700 flex items-center gap-2">
                  <svg className="w-5 h-5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  For the best experience, adjust your browser zoom to 75-80%. This ensures all case elements display properly and are easy to interact with.
                </p>
              </div>
              {(!hasStarted || timeExpired || finishedEarly) && (
                <button
                  onClick={() => {
                    setHasStarted(true);
                    setTimeLeft(1200);
                    setAnswers({});
                    setFeedback({});
                    setReveal({});
                    setTimeExpired(false);
                    setFinishedEarly(false);
                    setIsComplete(false);
                  }}
                  className="group relative mt-4 px-6 py-2.5 bg-gradient-to-r from-blue-600 to-blue-400 text-white rounded-xl shadow-md hover:shadow-lg transition-all duration-300 transform hover:scale-105"
                >
                  {!hasStarted ? 'Start Case' : 'Restart Case'}
                  <div className="absolute top-full mt-2 left-0 bg-gray-800 text-white text-xs px-3 py-2 rounded shadow-lg opacity-0 group-hover:opacity-100 transition-opacity duration-300 whitespace-nowrap pointer-events-auto">
                    {!hasStarted ? 'Click to unlock the questions and start your case timer.' : 'Click to start a new attempt.'}
                  </div>
                </button>
              )}
            </div>
          </div>
          <div className="absolute top-[85px] right-10 md:right-20 w-[300px] h-[400px] z-10 hidden md:block">
            {hasStarted && (timeExpired || isComplete) && (
              <div className={`absolute top-0 -left-[250px] bg-white/90 backdrop-blur-sm rounded-lg p-4 animate-pulse w-[220px] ${
                Math.round((totalCorrect / totalQuestions) * 100) < 65 
                ? 'shadow-[0_0_15px_rgba(239,68,68,0.2)]' 
                : 'shadow-[0_0_15px_rgba(59,130,246,0.2)]'
              }`}>
                <div className="relative">
                  <div className={`absolute inset-0 bg-gradient-to-r rounded-lg ${
                    Math.round((totalCorrect / totalQuestions) * 100) < 65
                    ? 'from-red-50/50 to-white/50'
                    : 'from-blue-50/50 to-white/50'
                  }`}></div>
                  <div className="relative space-y-2.5">
                    <h2 className="text-[16px] font-semibold text-gray-800 flex items-center gap-1.5">
                      <span className="text-lg">📊</span> Final Score
                    </h2>
                    <p className="text-[15px] text-gray-700">
                      Analysis: {numAnalysisCorrect}/{numAnalysisQuestions}
                    </p>
                    <p className="text-[15px] text-gray-700">
                      Report: {numReportCorrect}/{numReportQuestions}
                    </p>
                    <div className="w-full bg-gray-100 rounded-full h-2">
                      <div 
                        className={`rounded-full h-2 transition-all duration-300 ${
                          Math.round((totalCorrect / totalQuestions) * 100) < 65
                          ? 'bg-red-500'
                          : 'bg-blue-500'
                        }`}
                        style={{ width: `${Math.round((totalCorrect / totalQuestions) * 100)}%` }}
                      ></div>
                    </div>
                    <p className={`text-[14px] ${
                      Math.round((totalCorrect / totalQuestions) * 100) < 65
                      ? 'text-red-600'
                      : 'text-gray-600'
                    }`}>
                      Total: {totalCorrect}/{totalQuestions} ({Math.round((totalCorrect / totalQuestions) * 100)}% Accuracy)
                    </p>
                  </div>
                </div>
              </div>
            )}
            <div className="h-full bg-gray-300 rounded-xl flex items-center justify-center text-gray-500 shadow-md">
              <img 
                src={case4Image} 
                alt="SolarFlux City Case" 
                className="w-full h-full object-cover rounded-xl"
              />
            </div>
            {isNotebookOpen && hasBeenOpened && (
              <div className="w-[300px] bg-white rounded-xl shadow-[inset_0_2px_4px_rgba(0,0,0,0.1)] overflow-hidden transition-all duration-300 mt-4">
                <div className="p-3 border-b border-gray-100">
                  <div className="flex justify-between items-start mb-1">
                    <div>
                      <h3 className="text-base font-medium text-gray-700">Research Notes</h3>
                      <p className="text-[11px] text-gray-500 mt-0.5">
                        💡 Tip: Click power to collapse notebook & click floating to reopen the notebook wherever you are in the case
                      </p>
                    </div>
                    <button 
                      className={`text-gray-400 transition-colors duration-300 group relative p-1 ${
                        hasStarted ? 'hover:text-blue-600' : 'opacity-50 cursor-not-allowed'
                      }`}
                      onClick={() => hasStarted && handleNotebookToggle()}
                      disabled={!hasStarted}
                      title={hasStarted ? "Click to collapse notebook" : "Start case to enable notebook controls"}
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 3v6m0 6v6M5.636 5.636l4.243 4.243m4.242 4.242l4.243 4.243M3 12h6m6 0h6M5.636 18.364l4.243-4.243m4.242-4.242l4.243-4.243" />
                      </svg>
                      {hasStarted && (
                        <div className="absolute right-full mr-2 top-1/2 -translate-y-1/2 pointer-events-none">
                          <div className="bg-gray-800/80 backdrop-blur-sm text-white text-[11px] rounded-md px-2 py-1 opacity-0 group-hover:opacity-100 transition-opacity duration-300 whitespace-nowrap shadow-lg">
                            Click to collapse notebook
                          </div>
                        </div>
                      )}
                    </button>
                  </div>
                </div>
                <div 
                  className="notebook-content h-[300px] bg-gray-50 p-3 overflow-y-auto shadow-[inset_0_2px_4px_rgba(0,0,0,0.06)]"
                  onDragOver={(e) => {
                    if (!hasStarted) return;
                    e.preventDefault();
                    e.currentTarget.classList.add('bg-blue-50');
                  }}
                  onDragLeave={(e) => {
                    if (!hasStarted) return;
                    e.preventDefault();
                    e.currentTarget.classList.remove('bg-blue-50');
                  }}
                  onDrop={handleDrop}
                  contentEditable={hasStarted}
                  suppressContentEditableWarning={true}
                  style={{ outline: 'none', cursor: hasStarted ? 'text' : 'not-allowed' }}
                >
                  {!hasStarted && (
                    <div className="text-gray-500 text-sm italic">
                      Click "Start Case" to begin taking notes...
                    </div>
                  )}
                  {notebookItems.map((item, index) => (
                    <div 
                      key={index}
                      className="flex items-start gap-2 bg-white p-2 rounded shadow-[inset_0_1px_2px_rgba(0,0,0,0.05)] group mb-2"
                      contentEditable={false}
                    >
                      <div className="flex-1">
                        <div className="text-xs font-medium text-gray-900">{item.value}</div>
                        <div className="text-[10px] text-gray-500 mt-0.5">{item.label}</div>
                      </div>
                      <button
                        onClick={() => hasStarted && removeNotebookItem(index)}
                        className={`${hasStarted ? 'opacity-0 group-hover:opacity-100' : 'hidden'} transition-opacity text-gray-400 hover:text-red-500`}
                      >
                        <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
          {hasStarted && (
            <div className="fixed top-[85px] right-10 md:right-20 z-50">
              <div 
                className={`bg-white text-[15px] font-medium px-3 py-2 rounded-lg shadow-md flex items-center gap-1.5 ${
                  timeLeft <= (1200 * (1 - TIMER_WARNING_THRESHOLD)) && timeLeft > 0
                    ? 'animate-pulse-dramatic border-2 border-red-500'
                    : ''
                }`}
              >
                <span className={`text-base ${
                  timeLeft <= (1200 * (1 - TIMER_WARNING_THRESHOLD)) && timeLeft > 0
                    ? 'animate-bounce'
                    : ''
                }`}>⌛</span>
                <span className={`${
                  timeLeft <= (1200 * (1 - TIMER_WARNING_THRESHOLD)) && timeLeft > 0
                    ? 'text-red-500 font-bold'
                    : 'text-blue-600'
                }`}>
                  Time Left: {formatTime(timeLeft)}
                </span>
              </div>
            </div>
          )}
        </section>

        {/* Problem Section - Enhanced design */}
        <div id="problem" className="block h-0 -mt-[400px] pt-[400px]"></div>
        <section className="pt-20 pb-24 bg-gradient-to-br from-blue-100/60 via-blue-50/40 to-white relative">
          <div className="absolute inset-x-0 -top-20 h-20 bg-white"></div>
          <div className="container pl-[72px] grid grid-cols-1 md:grid-cols-2 gap-10">
            <div className="md:col-span-2 relative group" style={{ 
              paddingRight: 'calc(460px + 1.25rem)',
              maxWidth: 'calc(100% - 100px)'
            }}>
              <div className="absolute -top-20 inset-x-0 bottom-0 bg-white rounded-2xl rounded-t-none shadow-xl transform group-hover:scale-[1.02] transition-transform duration-300">
                <div className="absolute inset-0 bg-gradient-to-b from-transparent via-blue-50/30 to-blue-50/50 opacity-50" 
                     style={{ 
                       clipPath: 'polygon(0 25%, 100% 25%, 100% 100%, 0 100%)',
                       background: 'linear-gradient(to bottom, transparent 0%, rgba(59, 130, 246, 0.05) 50%, rgba(59, 130, 246, 0.1) 100%)'
                     }}>
                </div>
              </div>
              <div className="relative p-8">
                <h2 className="text-3xl font-bold mb-6 flex items-center gap-3 text-gray-900">
                  <span className="w-10 h-10 flex items-center justify-center bg-gradient-to-br from-blue-100 to-white rounded-lg shadow-inner">
                    📝
                  </span>
                  Problem
                </h2>
                <div className="space-y-4 prose prose-lg max-w-none">
                  <p className="text-gray-700 leading-relaxed">SolarFlux City is a fast-growing smart city in Northern Europe aiming to achieve net-zero carbon emissions by 2035. As part of its "Green Pulse 2.0" initiative, it rolled out solar panel installations across neighborhoods, launched an electric vehicle (EV) incentive program, and introduced dynamic energy pricing based on peak vs off-peak usage.</p>
                  <p className="text-gray-700 leading-relaxed">It's been 24 months since rollout, and the Sustainability Council has hired your consulting team to analyze whether these programs are working — and how to optimize the next phase.</p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Investigation Section */}
        <div id="investigation" className="block h-0 -mt-[400px] pt-[400px]"></div>
        <section className="py-12 bg-gray-50/80">
          <div className="container pl-[72px]">
            <h2 className="text-3xl font-bold mb-3 flex items-center gap-3">
              <span className="w-10 h-10 flex items-center justify-center bg-gradient-to-br from-blue-100 to-white rounded-lg shadow-inner">
                🔍
              </span>
              Investigation
            </h2>
            <p className="text-sm text-gray-600 mb-8 flex items-center gap-2">
              <span className="bg-blue-50 px-2 py-0.5 rounded">💡 Tip:</span>
              Drag and drop highlighted text into your notebook to collect important data points
            </p>
            <div className="flex gap-12 pr-[200px]">
              {/* Investigation Text */}
              <div className="w-[700px]">
                <div className="bg-white p-6 rounded-xl shadow-sm hover:shadow-lg transition-all duration-300 group relative space-y-5">
                  <p className="text-lg leading-relaxed">
                    During the 24-month trial, sensors recorded over <span 
                      className={`${hasStarted ? 'bg-blue-50 cursor-grab' : 'bg-gray-100 cursor-not-allowed'} px-2 py-1 rounded select-none`}
                      draggable={hasStarted}
                      onDragStart={(e) => hasStarted && handleDragStart(e, "2.3 million", "Total data points collected")}
                    >2.3 million</span> data points across the city. Exhibit 1 shows average solar panel production: SolarFlux City saw a <span 
                      className={`${hasStarted ? 'bg-blue-50 cursor-grab' : 'bg-gray-100 cursor-not-allowed'} px-2 py-1 rounded select-none`}
                      draggable={hasStarted}
                      onDragStart={(e) => hasStarted && handleDragStart(e, "14%", "Overall solar panel adoption")}
                    >14%</span> increase in solar panel adoption.
                  </p>
                  
                  <p className="text-lg leading-relaxed">
                    EV incentive program data (<span 
                      className={`${hasStarted ? 'bg-blue-50 cursor-grab' : 'bg-gray-100 cursor-not-allowed'} px-2 py-1 rounded select-none`}
                      draggable={hasStarted}
                      onDragStart={(e) => hasStarted && handleDragStart(e, "340", "Number of EV incentives")}
                    >340</span> incentives) revealed that <span 
                      className={`${hasStarted ? 'bg-blue-50 cursor-grab' : 'bg-gray-100 cursor-not-allowed'} px-2 py-1 rounded select-none`}
                      draggable={hasStarted}
                      onDragStart={(e) => hasStarted && handleDragStart(e, "61%", "Positive experience rating")}
                    >61%</span> of EV incentives were redeemed, indicating a high level of interest and satisfaction.
                  </p>

                  <p className="text-lg leading-relaxed">
                    Dynamic energy pricing data (<span 
                      className={`${hasStarted ? 'bg-blue-50 cursor-grab' : 'bg-gray-100 cursor-not-allowed'} px-2 py-1 rounded select-none`}
                      draggable={hasStarted}
                      onDragStart={(e) => hasStarted && handleDragStart(e, "500 kWh", "Total energy usage in April")}
                    >500 kWh</span>) showed that off-peak usage surpassed 400 kWh for the first time in <span 
                      className={`${hasStarted ? 'bg-blue-50 cursor-grab' : 'bg-gray-100 cursor-not-allowed'} px-2 py-1 rounded select-none`}
                      draggable={hasStarted}
                      onDragStart={(e) => hasStarted && handleDragStart(e, "April", "Month of first off-peak usage")}
                    >April</span>.
                  </p>

                  <p className="text-lg leading-relaxed">
                    Solar panel adoption data (<span 
                      className={`${hasStarted ? 'bg-blue-50 cursor-grab' : 'bg-gray-100 cursor-not-allowed'} px-2 py-1 rounded select-none`}
                      draggable={hasStarted}
                      onDragStart={(e) => hasStarted && handleDragStart(e, "31.6%", "Percentage of high-income solar panel adoptions")}
                    >31.6%</span>) revealed that high-income households accounted for <span 
                      className={`${hasStarted ? 'bg-blue-50 cursor-grab' : 'bg-gray-100 cursor-not-allowed'} px-2 py-1 rounded select-none`}
                      draggable={hasStarted}
                      onDragStart={(e) => hasStarted && handleDragStart(e, "Low Income", "Income group with the fewest solar panel adoptions")}
                    >Low Income</span> group had the fewest solar panel adoptions.
                  </p>

                  <p className="text-lg leading-relaxed">
                    CO₂ reduction data (<span 
                      className={`${hasStarted ? 'bg-blue-50 cursor-grab' : 'bg-gray-100 cursor-not-allowed'} px-2 py-1 rounded select-none`}
                      draggable={hasStarted}
                      onDragStart={(e) => hasStarted && handleDragStart(e, "Solar Panels", "Program with the most CO₂ reduction")}
                    >Solar Panels</span>) showed that the solar panel adoption program provided the most CO₂ reduction per million euros spent.
                  </p>
                </div>
              </div>

              {/* Interactive Notebook */}
              {isNotebookOpen ? (
                <div className="w-[500px] bg-white rounded-xl shadow-[inset_0_2px_4px_rgba(0,0,0,0.1)] overflow-hidden transition-all duration-300">
                  <div className="p-4 border-b border-gray-100">
                    <div className="flex justify-between items-start mb-2">
                      <div>
                        <h3 className="text-lg font-medium text-gray-700">Research Notes</h3>
                        <p className="text-xs text-gray-500 mt-1">
                          💡 Tip: Click power to collapse notebook & click floating button to reopen the notebook wherever you are in the case
                        </p>
                      </div>
                      <button 
                        className={`text-gray-400 transition-colors duration-300 group relative p-1 ${
                          hasStarted ? 'hover:text-blue-600' : 'opacity-50 cursor-not-allowed'
                        }`}
                        onClick={() => hasStarted && handleNotebookToggle()}
                        disabled={!hasStarted}
                        title={hasStarted ? "Click to collapse notebook" : "Start case to enable notebook controls"}
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 3v6m0 6v6M5.636 5.636l4.243 4.243m4.242 4.242l4.243 4.243M3 12h6m6 0h6M5.636 18.364l4.243-4.243m4.242-4.242l4.243-4.243" />
                        </svg>
                        {hasStarted && (
                          <div className="absolute right-full mr-2 top-1/2 -translate-y-1/2 pointer-events-none">
                            <div className="bg-gray-800/80 backdrop-blur-sm text-white text-[11px] rounded-md px-2 py-1 opacity-0 group-hover:opacity-100 transition-opacity duration-300 whitespace-nowrap shadow-lg">
                              Click to collapse notebook
                            </div>
                          </div>
                        )}
                      </button>
                    </div>
                  </div>
                  <div 
                    className="notebook-content h-[400px] bg-gray-50 p-4 overflow-y-auto shadow-[inset_0_2px_4px_rgba(0,0,0,0.06)]"
                    onDragOver={(e) => {
                      if (!hasStarted) return;
                      e.preventDefault();
                      e.currentTarget.classList.add('bg-blue-50');
                    }}
                    onDragLeave={(e) => {
                      if (!hasStarted) return;
                      e.preventDefault();
                      e.currentTarget.classList.remove('bg-blue-50');
                    }}
                    onDrop={handleDrop}
                    contentEditable={hasStarted}
                    suppressContentEditableWarning={true}
                    style={{ outline: 'none', cursor: hasStarted ? 'text' : 'not-allowed' }}
                  >
                    {!hasStarted && (
                      <div className="text-gray-500 text-sm italic">
                        Click "Start Case" to begin taking notes...
                      </div>
                    )}
                    {notebookItems.map((item, index) => (
                      <div 
                        key={index}
                        className="flex items-start gap-2 bg-white p-2.5 rounded shadow-[inset_0_1px_2px_rgba(0,0,0,0.05)] group mb-2"
                        contentEditable={false}
                      >
                        <div className="flex-1">
                          <div className="text-sm font-medium text-gray-900">{item.value}</div>
                          <div className="text-xs text-gray-500 mt-0.5">{item.label}</div>
                        </div>
                        <button
                          onClick={() => hasStarted && removeNotebookItem(index)}
                          className={`${hasStarted ? 'opacity-0 group-hover:opacity-100' : 'hidden'} transition-opacity text-gray-400 hover:text-red-500`}
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                          </svg>
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              ) : (
                <button
                  onClick={handleNotebookToggle}
                  className={`fixed right-24 top-1/2 -translate-y-1/2 bg-white p-4 rounded-xl shadow-[inset_0_2px_4px_rgba(0,0,0,0.1)] transform transition-all duration-300 group z-[999] ${
                    hasStarted 
                      ? 'hover:shadow-[inset_0_2px_8px_rgba(0,0,0,0.15)] hover:scale-105'
                      : 'opacity-50 cursor-not-allowed'
                  }`}
                  disabled={!hasStarted}
                >
                  <div className="relative">
                    {hasStarted && (
                      <div className="absolute -top-2 -right-2 w-4 h-4 bg-blue-500 rounded-full animate-pulse"></div>
                    )}
                    <svg 
                      className={`w-8 h-8 ${hasStarted ? 'text-gray-600 group-hover:text-blue-600' : 'text-gray-400'} transition-colors duration-300`}
                      fill="none" 
                      stroke="currentColor" 
                      viewBox="0 0 24 24"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                  </div>
                  {hasStarted && (
                    <div className="absolute right-full mr-2 top-1/2 -translate-y-1/2 pointer-events-none">
                      <div className="bg-gray-800/80 backdrop-blur-sm text-white text-sm rounded-md px-2 py-1 opacity-0 group-hover:opacity-100 transition-opacity duration-300 whitespace-nowrap shadow-lg">
                        Open notebook ({notebookItems.length} notes collected)
                      </div>
                    </div>
                  )}
                </button>
              )}
            </div>
          </div>
        </section>

        {/* Exhibits Section */}
        <div id="exhibits" className="block h-0 -mt-[400px] pt-[400px]"></div>
        <section className="py-12 bg-gradient-to-br from-blue-100/60 via-blue-50/40 to-white relative">
          <div className="absolute inset-x-0 -top-20 h-20 bg-gray-50/80"></div>
          <div className="container pl-[72px]" style={{ 
              paddingRight: 'calc(460px + 1.25rem)',
              maxWidth: 'calc(100% - 100px)'
            }}>
            <h2 className="text-3xl font-bold mb-4 flex items-center gap-3">
              <span className="w-10 h-10 flex items-center justify-center bg-gradient-to-br from-blue-100 to-white rounded-lg shadow-inner">
                📈
              </span>
              Exhibits
            </h2>
            <p className="text-sm text-gray-600 mb-4">Select an exhibit using the navigation below to analyze the data.</p>
            
            {/* Exhibit Navigation */}
            <div className="flex items-center gap-3 mb-4">
              {['1', '2a', '2b', '3', '4', '5'].map((exhibit) => (
                <button
                  key={exhibit}
                  onClick={() => hasStarted && setCurrentExhibit(exhibit)}
                  disabled={!hasStarted}
                  className={`px-6 py-2 rounded-xl shadow-md transition-all duration-300 transform ${
                    !hasStarted 
                      ? 'bg-gray-100 text-gray-400 cursor-not-allowed opacity-50'
                      : currentExhibit === exhibit
                        ? 'bg-gradient-to-r from-blue-600 to-blue-400 text-white hover:shadow-lg hover:scale-105'
                        : 'bg-white text-gray-600 hover:bg-blue-50 hover:shadow-lg hover:scale-105'
                  }`}
                >
                  {exhibit}
                </button>
              ))}
            </div>

            {/* Exhibit Display */}
            <div className={`bg-white rounded-xl shadow-sm p-6 transition-all duration-300 ${!hasStarted ? 'opacity-50' : 'hover:shadow-lg'}`}>
              {currentExhibit === '1' && (
                <>
                  <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                    <span className="text-blue-600">☀️</span>
                    Exhibit 1: Solar Output by District
                    {!hasStarted && (
                      <span className="text-xs text-gray-400 font-normal ml-2">(Start case to interact)</span>
                    )}
                  </h3>
                  <div className={`aspect-video bg-white rounded-lg flex items-center justify-center overflow-hidden ${!hasStarted ? 'filter blur-[2px]' : ''}`}>
                    <img 
                      src={require('../assets/RedRockCases/Case4/case4_ex1_solar_output.png')}
                      alt="Solar Output by District"
                      className="w-full h-full object-contain"
                    />
                  </div>
                  <p className="text-sm text-gray-500 mt-4">Solar power output (MWh) across different districts</p>
                </>
              )}

              {currentExhibit === '2a' && (
                <>
                <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                  <span className="text-blue-600">📊</span>
                    Exhibit 2a: Solar Adoption by Income Group
                    {!hasStarted && (
                      <span className="text-xs text-gray-400 font-normal ml-2">(Start case to interact)</span>
                    )}
                </h3>
                  <div className={`aspect-video bg-white rounded-lg flex items-center justify-center overflow-hidden ${!hasStarted ? 'filter blur-[2px]' : ''}`}>
                    <img 
                      src={require('../assets/RedRockCases/Case4/case4_ex2_adoption_income.png')}
                      alt="Solar Adoption by Income Group"
                      className="w-full h-full object-contain"
                    />
                </div>
                  <p className="text-sm text-gray-500 mt-4">Number of households adopting solar panels by income level</p>
                </>
              )}

              {currentExhibit === '2b' && (
                <>
                  <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                    <span className="text-blue-600">🚗</span>
                    Exhibit 2b: EV Purchases by Income Group
                    {!hasStarted && (
                      <span className="text-xs text-gray-400 font-normal ml-2">(Start case to interact)</span>
                    )}
                  </h3>
                  <div className={`aspect-video bg-white rounded-lg flex items-center justify-center overflow-hidden ${!hasStarted ? 'filter blur-[2px]' : ''}`}>
                    <img 
                      src={require('../assets/RedRockCases/Case4/case4_ex2_ev_income_pie.png')}
                      alt="EV Purchases by Income Group"
                      className="w-full h-full object-contain"
                    />
              </div>
                  <p className="text-sm text-gray-500 mt-4">Distribution of electric vehicle purchases across income groups</p>
                </>
              )}

              {currentExhibit === '3' && (
                <>
                <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                    <span className="text-blue-600">📈</span>
                    Exhibit 3: Off-Peak Power Usage Trend
                    {!hasStarted && (
                      <span className="text-xs text-gray-400 font-normal ml-2">(Start case to interact)</span>
                    )}
                </h3>
                  <div className={`aspect-video bg-white rounded-lg flex items-center justify-center overflow-hidden ${!hasStarted ? 'filter blur-[2px]' : ''}`}>
                    <img 
                      src={require('../assets/RedRockCases/Case4/case4_ex3_offpeak_usage.png')}
                      alt="Off-Peak Power Usage Over Time"
                      className="w-full h-full object-contain"
                    />
              </div>
                  <p className="text-sm text-gray-500 mt-4">Monthly trend of off-peak power consumption (kWh)</p>
                </>
              )}

              {currentExhibit === '4' && (
                <>
                  <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                    <span className="text-blue-600">🌱</span>
                    Exhibit 4: CO₂ Reduction vs Program Cost
                    {!hasStarted && (
                      <span className="text-xs text-gray-400 font-normal ml-2">(Start case to interact)</span>
                    )}
                  </h3>
                  <div className={`aspect-video bg-white rounded-lg flex items-center justify-center overflow-hidden ${!hasStarted ? 'filter blur-[2px]' : ''}`}>
                    <img 
                      src={require('../assets/RedRockCases/Case4/case4_ex4_cost_vs_co2.png')}
                      alt="CO2 Reduction vs Program Cost"
                      className="w-full h-full object-contain"
                    />
              </div>
                  <p className="text-sm text-gray-500 mt-4">Cost-effectiveness analysis of different carbon reduction programs</p>
                </>
              )}

              {currentExhibit === '5' && (
                <>
                  <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                    <span className="text-blue-600">⚡</span>
                    Exhibit 5: Total Energy Usage - April
                    {!hasStarted && (
                      <span className="text-xs text-gray-400 font-normal ml-2">(Start case to interact)</span>
                    )}
                  </h3>
                  <div className={`aspect-video bg-white rounded-lg flex items-center justify-center overflow-hidden ${!hasStarted ? 'filter blur-[2px]' : ''}`}>
                    <img 
                      src={require('../assets/RedRockCases/Case4/case4_ex5_energy_stacked.png')}
                      alt="Total Energy Usage - April"
                      className="w-full h-full object-contain"
                    />
                  </div>
                  <p className="text-sm text-gray-500 mt-4">Comparison of solar vs grid energy usage in April</p>
                </>
              )}
            </div>
          </div>
        </section>

        {/* Analysis Section */}
        <div id="questions" className="block h-0 -mt-[400px] pt-[400px]"></div>
        <section className="py-12 bg-gray-50/80">
          <div className="container pl-[72px]">
            <h2 className="text-3xl font-bold mb-8 flex items-center gap-3">
              <span className="w-10 h-10 flex items-center justify-center bg-gradient-to-br from-blue-100 to-white rounded-lg shadow-inner">
                🧠
              </span>
              Analysis
            </h2>
            <div className="space-y-6 md:pr-[460px]">
              {questions.map((q) => (
                <div 
                  key={q.id} 
                  className="bg-white p-6 rounded-xl shadow-sm hover:shadow-lg transition-all duration-300 group relative"
                >
                  <div className="flex items-start gap-4">
                    <div className="flex-shrink-0 w-8 h-8 rounded-full bg-gradient-to-br from-blue-600 to-blue-400 flex items-center justify-center text-white font-medium shadow-md">
                      {q.id}
                    </div>
                    <div className="flex-grow">
                      <h3 className="text-lg font-medium transition-all duration-300 hover:font-bold hover:text-blue-600 mb-4">
                        {q.text}
                      </h3>
                      {q.type === 'text' ? (
                        <div className="space-y-3">
                          <div className="flex gap-3">
                        <input
                          type="text"
                              value={answers[q.id] || ''}
                              onChange={(e) => handleInput(q.id, e.target.value)}
                              onKeyDown={(e) => e.key === 'Enter' && handleSubmit(q.id)}
                              className="flex-grow px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-300 hover:border-blue-400"
                              placeholder="Type your answer and press Enter..."
                          disabled={!hasStarted || timeExpired || finishedEarly}
                        />
                            <button
                              onClick={() => handleSubmit(q.id)}
                              className="px-4 py-2 bg-gradient-to-r from-blue-600 to-blue-400 text-white rounded-lg shadow-md hover:shadow-lg transition-all duration-300 transform hover:scale-105"
                              disabled={!hasStarted || timeExpired || finishedEarly}
                            >
                              Submit
                            </button>
                          </div>
                          <div className="flex gap-2">
                            {feedback[q.id] === 'incorrect' && hasStarted && !timeExpired && !finishedEarly && (
                              <button
                                onClick={() => handleTryAgain(q.id)}
                                className="px-3 py-1.5 bg-gradient-to-r from-gray-600 to-gray-500 text-white rounded-lg shadow-md hover:shadow-lg transition-all duration-300 transform hover:scale-105"
                              >
                                Try Again
                              </button>
                            )}
                            {(timeExpired || finishedEarly) && (
                              <button
                                onClick={() => handleReveal(q.id)}
                                className="px-3 py-1.5 bg-gradient-to-r from-blue-600 to-blue-400 text-white rounded-lg shadow-md hover:shadow-lg transition-all duration-300 transform hover:scale-105"
                              >
                                See Answer
                              </button>
                            )}
                          </div>
                          {feedback[q.id] && (
                            <div className={`mt-4 p-3 rounded-lg ${
                              feedback[q.id] === 'correct' ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'
                            }`}>
                              {feedback[q.id] === 'correct' ? 'Correct!' : 'Incorrect. Try again.'}
                            </div>
                          )}
                          {reveal[q.id] && (
                            <div className="mt-2 p-3 bg-blue-50 rounded-lg">
                              <p className="text-blue-700">🔍 Answer: <strong>{q.answer}</strong></p>
                            </div>
                          )}
                        </div>
                      ) : q.type === 'multi' ? (
                        <div className="space-y-3">
                          <div className="flex flex-col gap-3">
                            {q.parts.map((part, index) => (
                              <div key={index} className="flex gap-3">
                                <div className="flex-grow">
                                  <label className="block text-sm font-medium text-gray-700 mb-1">{part.label}</label>
                                  <input
                                    type="text"
                                    value={((answers[q.id] || {})[index] || '')}
                                    onChange={(e) => handleInput(q.id, e.target.value, index)}
                                    onKeyDown={(e) => e.key === 'Enter' && handleSubmit(q.id)}
                                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-300 hover:border-blue-400"
                                    placeholder={`Enter ${part.label.toLowerCase()}...`}
                                    disabled={!hasStarted || timeExpired || finishedEarly}
                                  />
                                </div>
                              </div>
                            ))}
                            <button
                              onClick={() => handleSubmit(q.id)}
                              className="px-4 py-2 bg-gradient-to-r from-blue-600 to-blue-400 text-white rounded-lg shadow-md hover:shadow-lg transition-all duration-300 transform hover:scale-105"
                              disabled={!hasStarted || timeExpired || finishedEarly}
                            >
                              Submit
                            </button>
                          </div>
                          <div className="flex gap-2">
                            {feedback[q.id] === 'incorrect' && hasStarted && !timeExpired && !finishedEarly && (
                              <button
                                onClick={() => handleTryAgain(q.id)}
                                className="px-3 py-1.5 bg-gradient-to-r from-gray-600 to-gray-500 text-white rounded-lg shadow-md hover:shadow-lg transition-all duration-300 transform hover:scale-105"
                              >
                                Try Again
                              </button>
                            )}
                            {(timeExpired || finishedEarly) && (
                              <button
                                onClick={() => handleReveal(q.id)}
                                className="px-3 py-1.5 bg-gradient-to-r from-blue-600 to-blue-400 text-white rounded-lg shadow-md hover:shadow-lg transition-all duration-300 transform hover:scale-105"
                              >
                                See Answer
                              </button>
                            )}
                          </div>
                          {feedback[q.id] && (
                            <div className={`mt-4 p-3 rounded-lg ${
                              feedback[q.id] === 'correct' ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'
                            }`}>
                              {feedback[q.id] === 'correct' ? 'Correct!' : 'Incorrect. Try again.'}
                            </div>
                          )}
                          {reveal[q.id] && (
                            <div className="mt-2 p-3 bg-blue-50 rounded-lg">
                              <p className="text-blue-700">
                                🔍 Answer: 
                                {q.parts.map((part, index) => (
                                  <span key={index}>
                                    <br />
                                    <strong>{part.label}</strong>: {part.answer}
                                  </span>
                                ))}
                              </p>
                            </div>
                          )}
                        </div>
                      ) : q.type === 'mcq' ? (
                        <div className="space-y-2">
                          {q.options.map((option) => (
                            <button
                              key={option}
                              onClick={() => handleMCQSelect(q.id, option)}
                              className={`w-full text-left px-4 py-2 rounded-lg border transition-all duration-300 transform hover:scale-102 ${
                                answers[q.id] === option
                                  ? 'bg-gradient-to-r from-blue-600 to-blue-400 text-white border-transparent'
                                  : 'border-gray-300 hover:border-blue-400 hover:bg-blue-50'
                              }`}
                              disabled={!hasStarted || timeExpired || finishedEarly}
                            >
                              {option}
                            </button>
                          ))}
                          <div className="flex gap-2 mt-3">
                            {feedback[q.id] === 'incorrect' && hasStarted && !timeExpired && !finishedEarly && (
                              <button
                                onClick={() => handleTryAgain(q.id)}
                                className="px-3 py-1.5 bg-gradient-to-r from-gray-600 to-gray-500 text-white rounded-lg shadow-md hover:shadow-lg transition-all duration-300 transform hover:scale-105"
                              >
                                Try Again
                              </button>
                            )}
                            {(timeExpired || finishedEarly) && (
                              <button
                                onClick={() => handleReveal(q.id)}
                                className="px-3 py-1.5 bg-gradient-to-r from-blue-600 to-blue-400 text-white rounded-lg shadow-md hover:shadow-lg transition-all duration-300 transform hover:scale-105"
                              >
                                See Answer
                              </button>
                            )}
                          </div>
                          {feedback[q.id] && (
                            <div className={`mt-4 p-3 rounded-lg ${
                              feedback[q.id] === 'correct' ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'
                            }`}>
                              {feedback[q.id] === 'correct' ? 'Correct!' : 'Incorrect. Try again.'}
                          </div>
                        )}
                          {reveal[q.id] && (
                            <div className="mt-2 p-3 bg-blue-50 rounded-lg">
                              <p className="text-blue-700">🔍 Answer: <strong>{q.answer}</strong></p>
                            </div>
                        )}
                      </div>
                      ) : null}
                    </div>
                  </div>
                </div>
              ))}

                {finishedEarly && (
                  <div className="mt-6 text-green-600 text-base font-semibold">
                  🎉 Congratulations! You completed all questions before the timer ran out! Amazing work!
                  </div>
                )}
            </div>
          </div>
        </section>

        {/* Report Section */}
        <div id="report" className="block h-0 -mt-[400px] pt-[400px]"></div>
        <section className="py-20 bg-gradient-to-br from-blue-50/40 via-blue-50/20 to-white">
          <div className="container pl-[72px]">
            <h2 className="text-3xl font-bold mb-8 flex items-center gap-3">
              <span className="w-10 h-10 flex items-center justify-center bg-gradient-to-br from-blue-100 to-white rounded-lg shadow-inner">
                📊
              </span>
              Report
            </h2>
            <div className="space-y-6 md:pr-[460px]">
              <div className="bg-white p-6 rounded-xl shadow-sm hover:shadow-lg transition-all duration-300 group relative">
                <div className="flex items-start gap-4">
                  <div className="flex-grow">
                    <div className="space-y-3">
                      <div className="text-lg leading-relaxed text-justify" style={{ lineHeight: '2' }}>
                        Central district achieved the highest solar energy production increase at <input
                          type="text"
                          value={answers['report_solar_increase'] || ''}
                          onChange={(e) => handleInput('report_solar_increase', e.target.value)}
                          className={`w-24 mx-2 px-4 py-2 border-2 rounded-md focus:outline-none focus:ring-2 transition-all duration-300 text-lg ${
                            (timeExpired || finishedEarly) && feedback['report_solar_increase'] === 'correct' ? 'border-green-500 bg-green-50 ring-green-200' :
                            (timeExpired || finishedEarly) && feedback['report_solar_increase'] === 'incorrect' ? 'border-red-500 bg-red-50 ring-red-200' :
                            'border-gray-300 focus:ring-blue-200'
                          }`}
                          disabled={!hasStarted || timeExpired || finishedEarly}
                          onKeyDown={(e) => e.key === 'Enter' && handleReportSubmit()}
                        /> MWh, while <input
                          type="text"
                          value={answers['report_income_group'] || ''}
                          onChange={(e) => handleInput('report_income_group', e.target.value)}
                          className={`w-32 mx-2 px-4 py-2 border-2 rounded-md focus:outline-none focus:ring-2 transition-all duration-300 text-lg ${
                            (timeExpired || finishedEarly) && feedback['report_income_group'] === 'correct' ? 'border-green-500 bg-green-50 ring-green-200' :
                            (timeExpired || finishedEarly) && feedback['report_income_group'] === 'incorrect' ? 'border-red-500 bg-red-50 ring-red-200' :
                            'border-gray-300 focus:ring-blue-200'
                          }`}
                          disabled={!hasStarted || timeExpired || finishedEarly}
                          onKeyDown={(e) => e.key === 'Enter' && handleReportSubmit()}
                        /> income households led in adoption numbers. The city saw a meaningful shift in consumption, with off-peak usage first surpassing 400 kWh in <input
                          type="text"
                          value={answers['report_month'] || ''}
                          onChange={(e) => handleInput('report_month', e.target.value)}
                          className={`w-24 mx-2 px-4 py-2 border-2 rounded-md focus:outline-none focus:ring-2 transition-all duration-300 text-lg ${
                            (timeExpired || finishedEarly) && feedback['report_month'] === 'correct' ? 'border-green-500 bg-green-50 ring-green-200' :
                            (timeExpired || finishedEarly) && feedback['report_month'] === 'incorrect' ? 'border-red-500 bg-red-50 ring-red-200' :
                            'border-gray-300 focus:ring-blue-200'
                          }`}
                          disabled={!hasStarted || timeExpired || finishedEarly}
                          onKeyDown={(e) => e.key === 'Enter' && handleReportSubmit()}
                        />. EV incentives yielded less CO2 savings per euro than solar infrastructure, with solar proving <input
                          type="text"
                          value={answers['report_efficiency'] || ''}
                          onChange={(e) => handleInput('report_efficiency', e.target.value)}
                          className={`w-24 mx-2 px-4 py-2 border-2 rounded-md focus:outline-none focus:ring-2 transition-all duration-300 text-lg ${
                            (timeExpired || finishedEarly) && feedback['report_efficiency'] === 'correct' ? 'border-green-500 bg-green-50 ring-green-200' :
                            (timeExpired || finishedEarly) && feedback['report_efficiency'] === 'incorrect' ? 'border-red-500 bg-red-50 ring-red-200' :
                            'border-gray-300 focus:ring-blue-200'
                          }`}
                          disabled={!hasStarted || timeExpired || finishedEarly}
                          onKeyDown={(e) => e.key === 'Enter' && handleReportSubmit()}
                        /> times more efficient. Cost per kWh was <input
                          type="text"
                          value={answers['report_cost'] || ''}
                          onChange={(e) => handleInput('report_cost', e.target.value)}
                          className={`w-24 mx-2 px-4 py-2 border-2 rounded-md focus:outline-none focus:ring-2 transition-all duration-300 text-lg ${
                            (timeExpired || finishedEarly) && feedback['report_cost'] === 'correct' ? 'border-green-500 bg-green-50 ring-green-200' :
                            (timeExpired || finishedEarly) && feedback['report_cost'] === 'incorrect' ? 'border-red-500 bg-red-50 ring-red-200' :
                            'border-gray-300 focus:ring-blue-200'
                          }`}
                          disabled={!hasStarted || timeExpired || finishedEarly}
                          onKeyDown={(e) => e.key === 'Enter' && handleReportSubmit()}
                        /> EUR off-peak, reinforcing the effectiveness of the time-based pricing model.
                      </div>
                      <div className="flex gap-2">
                        {(!timeExpired && !finishedEarly) && (
                          <button
                            onClick={() => handleReportSubmit()}
                            className="px-4 py-2 bg-gradient-to-r from-blue-600 to-blue-400 text-white rounded-lg shadow-md hover:shadow-lg transition-all duration-300 transform hover:scale-105"
                            disabled={!hasStarted || timeExpired || finishedEarly}
                          >
                            Submit Answers
                          </button>
                        )}
                        {(timeExpired || finishedEarly) && (
                          <button
                            onClick={() => handleReportReveal()}
                            className="px-3 py-1.5 bg-gradient-to-r from-blue-600 to-blue-400 text-white rounded-lg shadow-md hover:shadow-lg transition-all duration-300 transform hover:scale-105"
                          >
                            See Answers
                          </button>
                        )}
                      </div>
                      {(timeExpired || finishedEarly) && feedback['report'] && (
                        <div className={`mt-4 p-3 rounded-lg ${
                          Object.values(feedback).every(f => f === 'correct') 
                            ? 'bg-green-50 text-green-700' 
                            : 'bg-red-50 text-red-700'
                        }`}>
                          {Object.values(feedback).every(f => f === 'correct') 
                            ? 'All answers are correct!' 
                            : 'Some answers are incorrect. Please check your answers.'}
                        </div>
                      )}
                      {reveal['report'] && (
                        <div className="mt-2 p-3 bg-blue-50 rounded-lg">
                          <p className="text-blue-700">
                            🔍 Answers:<br/>
                            Solar Energy Increase: 900 MWh<br/>
                            Income Group: Middle<br/>
                            Month: April<br/>
                            Efficiency: 2 times<br/>
                            Off-peak Cost: 0.08 EUR
                          </p>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {finishedEarly && (
                <div className="mt-6 text-green-600 text-base font-semibold">
                  🎉 Congratulations! You completed the report before the timer ran out! Amazing work!
                </div>
              )}
              {timeExpired && !finishedEarly && (
                <div className="mt-6 text-red-600 text-base font-semibold">
                  ❌ Time's up! You can still view the answers by clicking "See Answer".
                </div>
              )}
            </div>
          </div>
        </section>

        {/* Recommendation Section - Subtle off-white */}
        <div id="recommendation" className="block h-0 -mt-[400px] pt-[400px]"></div>
        <section className="py-20 bg-gray-50/80">
          <div className="container pl-[72px] grid grid-cols-1 md:grid-cols-2 gap-10">
            <div className="md:col-span-2 md:pr-[460px]">
              <h2 className="text-3xl font-bold mb-6 flex items-center gap-3">
                <span className="w-10 h-10 flex items-center justify-center bg-gradient-to-br from-blue-100 to-white rounded-lg shadow-inner">
                  💡
                </span>
                Recommendation
              </h2>
              <ul className="space-y-4">
                <li className="flex items-start">
                  <span className="mr-3 text-xl">📊</span>
                  <span>Expand solar panel installations to underserved areas</span>
                </li>
                <li className="flex items-start">
                  <span className="mr-3 text-xl">👥</span>
                  <span>Focus EV incentive programs on middle-income households</span>
                </li>
                <li className="flex items-start">
                  <span className="mr-3 text-xl">🎯</span>
                  <span>Revise dynamic energy pricing to encourage off-peak usage</span>
                </li>
                <li className="flex items-start">
                  <span className="mr-3 text-xl">♻️</span>
                  <span>Promote solar panel adoption to high-income groups</span>
                </li>
              </ul>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
};

export default Case4Page; 

<style jsx global>{`
    @keyframes pulse-dramatic {
      0%, 100% {
        transform: scale(1);
        box-shadow: 0 0 0 0 rgba(239, 68, 68, 0.4);
      }
      50% {
        transform: scale(1.15);
        box-shadow: 0 0 0 10px rgba(239, 68, 68, 0);
      }
    }
    
    .animate-pulse-dramatic {
      animation: pulse-dramatic 1s cubic-bezier(0.4, 0, 0.6, 1) infinite;
    }

    @keyframes bounce {
      0%, 100% {
        transform: translateY(0) scale(1);
      }
      50% {
        transform: translateY(-5px) scale(1.2);
      }
    }

    .animate-bounce {
      animation: bounce 0.5s ease infinite;
    }
  `}</style>